import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, TrendingUp, TrendingDown } from "lucide-react";

const CostCalculator = () => {
  const [inputs, setInputs] = useState({
    product_cost_rmb: 10000,
    shipping_usd: 500,
    customs_usd: 200,
    djibouti_usd: 150,
    agent_usd: 100,
    other_usd: 50,
    ethiopia_transport_etb: 5000,
    rmb_to_usd: 0.14,
    usd_to_etb: 128,
    expected_revenue_etb: 300000,
  });

  const update = (field, val) => setInputs(prev => ({ ...prev, [field]: parseFloat(val) || 0 }));

  const calc = useMemo(() => {
    const productUSD = inputs.product_cost_rmb * inputs.rmb_to_usd;
    const ethTransportUSD = inputs.usd_to_etb ? inputs.ethiopia_transport_etb / inputs.usd_to_etb : 0;
    const totalUSD = productUSD + inputs.shipping_usd + inputs.customs_usd + 
                     inputs.djibouti_usd + inputs.agent_usd + inputs.other_usd + ethTransportUSD;
    const totalETB = totalUSD * inputs.usd_to_etb;
    const profit = inputs.expected_revenue_etb - totalETB;
    const margin = inputs.expected_revenue_etb ? (profit / inputs.expected_revenue_etb) * 100 : 0;
    const roi = totalETB ? (profit / totalETB) * 100 : 0;
    
    return { productUSD, ethTransportUSD, totalUSD, totalETB, profit, margin, roi };
  }, [inputs]);

  const inputRows = [
    { key: "product_cost_rmb", label: "Product Cost (RMB ¥)", prefix: "¥" },
    { key: "shipping_usd", label: "Shipping (USD $)", prefix: "$" },
    { key: "customs_usd", label: "Customs Duty (USD $)", prefix: "$" },
    { key: "djibouti_usd", label: "Djibouti Fees (USD $)", prefix: "$" },
    { key: "agent_usd", label: "Agent Fees (USD $)", prefix: "$" },
    { key: "other_usd", label: "Other Fees (USD $)", prefix: "$" },
    { key: "ethiopia_transport_etb", label: "Ethiopia Transport (ETB)", prefix: "" },
  ];

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Calculator</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Import Cost Calculator</h1>
          <p className="text-white/50 mt-2">Calculate your total costs and profit margins</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-3 trade-card rounded-sm p-6"
          >
            <h3 className="text-sm uppercase tracking-widest trade-gold mb-6">Inputs</h3>
            
            <div className="space-y-4">
              {inputRows.map(row => (
                <div key={row.key}>
                  <Label className="text-xs text-white/60">{row.label}</Label>
                  <div className="relative mt-1">
                    {row.prefix && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">
                        {row.prefix}
                      </span>
                    )}
                    <Input
                      type="number"
                      value={inputs[row.key]}
                      onChange={e => update(row.key, e.target.value)}
                      className={`trade-input h-11 rounded-sm ${row.prefix ? "pl-7" : ""}`}
                    />
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t border-white/5 grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">1 RMB = ? USD</Label>
                  <Input type="number" step="0.0001" value={inputs.rmb_to_usd} onChange={e => update("rmb_to_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">1 USD = ? ETB</Label>
                  <Input type="number" step="0.01" value={inputs.usd_to_etb} onChange={e => update("usd_to_etb", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
              </div>

              <div className="pt-4 border-t border-white/5">
                <Label className="text-xs trade-green">Expected Revenue (ETB)</Label>
                <Input type="number" value={inputs.expected_revenue_etb} onChange={e => update("expected_revenue_etb", e.target.value)} className="trade-input mt-1 h-11 rounded-sm text-lg font-mono" />
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-5 lg:sticky lg:top-28 h-fit"
          >
            {/* Big Profit Card */}
            <div className={`trade-card rounded-sm p-8 relative overflow-hidden ${calc.profit >= 0 ? "border-[#10b981]/30" : "border-red-500/30"}`}>
              <div className={`absolute inset-x-0 top-0 h-1 ${calc.profit >= 0 ? "bg-[#10b981]" : "bg-red-500"}`} />
              <p className="text-xs uppercase tracking-[0.3em] mb-3 text-white/60">Net Profit (ETB)</p>
              <p className={`text-4xl md:text-5xl font-serif font-bold mb-4 ${calc.profit >= 0 ? "trade-green" : "text-red-400"}`}>
                {calc.profit >= 0 ? "+" : ""}
                {calc.profit.toLocaleString(undefined, {maximumFractionDigits: 0})}
              </p>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  {calc.margin >= 0 ? <TrendingUp size={14} className="trade-green" /> : <TrendingDown size={14} className="text-red-400" />}
                  <span className={calc.margin >= 0 ? "trade-green" : "text-red-400"}>
                    {calc.margin.toFixed(1)}% margin
                  </span>
                </span>
                <span className="text-white/40">·</span>
                <span className="text-white/60">{calc.roi.toFixed(1)}% ROI</span>
              </div>
            </div>

            {/* Breakdown */}
            <div className="trade-card rounded-sm p-6">
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-5">Cost Breakdown</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Product (USD)</span>
                  <span className="font-mono">${calc.productUSD.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Shipping</span>
                  <span className="font-mono">${inputs.shipping_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Customs</span>
                  <span className="font-mono">${inputs.customs_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Djibouti</span>
                  <span className="font-mono">${inputs.djibouti_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Agent + Other</span>
                  <span className="font-mono">${(inputs.agent_usd + inputs.other_usd).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">ET Transport</span>
                  <span className="font-mono">${calc.ethTransportUSD.toFixed(2)}</span>
                </div>
                <div className="pt-3 border-t border-white/10 flex justify-between">
                  <span className="trade-gold font-semibold">Total USD</span>
                  <span className="trade-gold font-mono font-bold">${calc.totalUSD.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="trade-gold font-semibold">Total ETB</span>
                  <span className="trade-gold font-mono font-bold">
                    {calc.totalETB.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default CostCalculator;
